import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SvgIcon from "../assest/svg/svg.jsx";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Eye, EyeOff, Fingerprint, ScanFace, Shield, Trash2 } from "lucide-react";
import Swal from "sweetalert2";
import {
  getUserBiometrics,
  checkFingerprintStatus,
  scanFingerprint,
  registerBiometric,
  deleteBiometric,
  registerFingerprintBiometric,
  getUserBiometrics2,
  deleteBiometric2,
} from '../api/services.js';
import CameraCapture from "../components/camera.jsx";
import DeleteConfirmationModal from "../components/deleteConfirmationModal.jsx";

const ProfileSettings = ({ dir }) => {

  const [loadingFaceButton, setLoadingFaceButton] = useState(false);
  const [loadingFingerprintButton, setLoadingFingerprintButton] = useState(false);
  const storage = localStorage.getItem("profileData") || sessionStorage.getItem("profileData");
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const userId = JSON.parse(storage)?.user?.id || JSON.parse(storage)?.id;
  const base_url = process.env.REACT_APP_PORTAL_APP_BASE_URL;
  const [showCameraModal, setShowCameraModal] = useState({
    isOpen: false,
    transactionId: null
  });

  const [profileData, setProfileData] = useState({
    name: "",
    employeeId: "",
    email: "",
    company: null,
    department: "",
    initial: "N",
    avatar: "",
    doj: "",
  });

  const [formData, setFormData] = useState({
    oldPassword: "",
    password: "",
    confirmPassword: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    password: false,
    confirmPassword: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [registeredBiometrics, setRegisteredBiometrics] = useState({
    FACE: false,
    FINGERPRINT: false,
  });
  const [biometricLoading, setBiometricLoading] = useState(null);
  const navigate = useNavigate()
  const { t } = useTranslation();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value.replace(/\s{2,}/g, " "),
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const extractFaceTemplate = (captureData, transactionId) => {
    return (
      captureData?.data?.image ||
      captureData?.data?.transaction_id ||
      captureData?.transaction_id ||
      `FACE_CAPTURED_${transactionId}`
    );
  };

  const extractFingerprintTemplate = (scanData) => {
    return scanData?.data?.fingerprintIso || scanData?.data?.fingerprintWsq || '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const oldPassword = formData.oldPassword.trim();
    const password = formData.password.trim();
    const confirmPassword = formData.confirmPassword.trim();
    if (!oldPassword || !password || !confirmPassword) {
      setError(t("Fields are required"));
      return;
    }
    else if (password !== confirmPassword) {
      setError(t("password_does_not_match"));
      return;
    }
    else if (oldPassword === password) {
      setError(t("new_pass_different"));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_PORTAL_APP_BASE_URL}/auth/tokenPassword`,
        {
          oldPassword: oldPassword,
          password: password,
          confirmPassword: confirmPassword,
        },
        {
          headers: {
            token: `${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
          },
        }
      );
      setFormData({
        oldPassword: "",
        password: "",
        confirmPassword: "",
      });
      // setShowModal(true);
      setTimeout(() => {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("profileData");
        sessionStorage.removeItem("user");
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("profileData");
        sessionStorage.removeItem("conversation");
        sessionStorage.removeItem("currentSessionId");
        sessionStorage.removeItem("sessionNumber");
        sessionStorage.removeItem("sessions");
        navigate("/login");
      }, 1000);
    } catch (err) {
      setError(err?.response?.data?.message || "Error resetting password.");
    } finally {
      setLoading(false);
    }
  };

  const getProfileData = useCallback(async () => {
    try {
      const response = await axios.get(`${base_url}/users/profile`, { headers: { Authorization: `${token}` } });
      const profileData = response.data.data;
      setProfileData((prev) => ({
        ...prev,
        name: profileData.name || "N/A",
        employeeId: profileData.employeeId || "N/A",
        email: profileData.email || "N/A",
        company: profileData.company || null,
        department: profileData.department || "",
        initial: profileData.name ? profileData.name.charAt(0) : "N",
        avatar: profileData.avatar || "",
        doj: profileData.doj || "",
      }));
    } catch (error) {
      console.log(error);
    }
  }, [base_url, token])

  const loadRegisteredBiometrics = useCallback(async () => {
    if (!userId) return;
    try {
      setLoadingFaceButton(true);
      setLoadingFingerprintButton(true);
      const [response, response2] = await Promise.all([
        getUserBiometrics(userId),
        getUserBiometrics2(userId)
      ]);
      setRegisteredBiometrics({
        FINGERPRINT: response?.fingerprints?.some((row) => row.type === "fingerprint") ?? false,
        FACE: response2?.data?.some((row) => row.type === 'FACE') ?? false,
      });
      setLoadingFaceButton(false);
      setLoadingFingerprintButton(false);
    } catch (e) {
      setLoadingFaceButton(false);
      setLoadingFingerprintButton(false);
      console.error('Failed to load registered biometrics', e);
    }
  }, [userId]);

  const handleFaceCapture = async (imageSrc) => {
    try {
      const res = await registerBiometric(userId, 'FACE', imageSrc);
      console.log('handleFaceCapture ===>', res);
      setShowCameraModal({ isOpen: false });
      Swal.fire({
        icon: 'success',
        title: t('Face registered successfully'),
        timer: 1800,
        customClass: {
          confirmButton: 'gradient-button'
        }
      });
    } catch (error) {
      if (error.ai.response.data.message === 'FACE enrollemnt already completed') {
        setShowCameraModal({ isOpen: false });
        Swal.fire({
          icon: 'error',
          title: t('FACE enrollemnt already completed'),
          timer: 1800,
          customClass: {
            confirmButton: 'gradient-button'
          }
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: t('Something went wrong!'),
          timer: 1800,
          customClass: {
            confirmButton: 'gradient-button'
          }
        });
      }
      console.log(error);
    }
  };

  // Handle camera modal close
  const handleCameraClose = (success) => {
    setShowCameraModal({ isOpen: false });
    if (success) {
      // Proceed with registration
      completeFaceRegistration();
    }
  };

  const registerFace = async () => {

    try {
      setBiometricLoading('FACE');

      // Generate transaction ID and open camera
      const transactionId = `${Date.now()}_${userId}_${Math.floor(1000000000 + Math.random() * 9000000000)}`;

      setShowCameraModal({
        isOpen: true,
        transactionId
      });

    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: t('Camera Error'),
        text: error?.message || t('Please try again.'),
        customClass: {
          confirmButton: 'gradient-button'
        }
      });
      setBiometricLoading(null);
    }
  };

  const completeFaceRegistration = async (captureData) => {
    try {
      const template = extractFaceTemplate(captureData, showCameraModal.transactionId);
      await registerBiometric(userId, 'FACE', template);
      await loadRegisteredBiometrics();

      Swal.fire({
        icon: 'success',
        title: t('Face registered successfully'),
        timer: 1800,
        customClass: {
          confirmButton: 'gradient-button'
        }
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: t('Face registration failed'),
        text: error?.message || t('Please try again.'),
        customClass: {
          confirmButton: 'gradient-button'
        }
      });
    } finally {
      setBiometricLoading(null);
    }
  };

  const registerFingerprint = async () => {
    const arr = [];
    let scanResult = null;
    let scanError = null;

    if (!userId) {
      Swal.fire({
        icon: 'error',
        title: t('User not found'),
        text: t('Unable to identify current user profile.'),
        customClass: {
          confirmButton: 'gradient-button'
        }
      });
      return;
    }
    try {
      setBiometricLoading('FINGERPRINT');

      // Show scanning progress modal
      let progressModal;

      // Create a custom progress modal
      progressModal = Swal.fire({
        title: t('Scanning Fingerprint'),
        html: `
        <div class="fingerprint-scan-container">
          <div class="fingerprint-icon mb-4">
            <svg class="animate-pulse" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#B03982" stroke-width="1.5">
              <path d="M12 3a9 9 0 0 0-9 9v3c0 .8.4 1.5 1 1.9M21 15v-3a9 9 0 0 0-9-9" />
              <path d="M3 15v3a9 9 0 0 0 9 9 9 9 0 0 0 9-9v-3" />
              <path d="M12 12v6" />
              <path d="M8 15v3" />
              <path d="M16 15v3" />
            </svg>
          </div>
          <div class="progress-bar-container w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
            <div id="progress-bar" class="h-full bg-gradient-to-r from-[#A73884] to-[#803A85] transition-all duration-300" style="width: 0%"></div>
          </div>
          <div id="progress-text" class="text-sm text-gray-600">0%</div>
          <div class="mt-2 text-xs text-gray-500">${t('Please keep your finger on the scanner')}</div>
          <div class="mt-1 text-xs text-gray-400" id="attempt-counter">Attempt 1/10</div>
        </div>
      `,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          // You can add any initialization here if needed
        }
      });

      const scannerStatus = await checkFingerprintStatus();
      if (scannerStatus?.code !== '0000') {
        throw new Error(
          scannerStatus?.message || 'Fingerprint scanner is not available',
        );
      }

      // Function to update progress
      const updateProgress = (percent, attemptNumber) => {
        const progressBar = document.getElementById('progress-bar');
        const progressText = document.getElementById('progress-text');
        const attemptCounter = document.getElementById('attempt-counter');
        if (progressBar && progressText) {
          progressBar.style.width = `${percent}%`;
          progressText.textContent = `${percent}%`;
        }
        if (attemptCounter && attemptNumber) {
          attemptCounter.textContent = `Attempt ${attemptNumber}/10`;
        }
      };

      // Function to call when 100% progress is reached
      const onProgressComplete = async (scanData) => {
        console.log('✅ Scan completed at 100%', scanData);

        // Optional: Add any animation or visual feedback
        const fingerprintIcon = document.querySelector('.fingerprint-icon svg');
        if (fingerprintIcon) {
          fingerprintIcon.classList.remove('animate-pulse');
          fingerprintIcon.classList.add('animate-bounce');
        }

        // Small delay to show completion animation
        await new Promise(resolve => setTimeout(resolve, 500));

        // Close the progress modal
        if (progressModal) {
          Swal.close();
        }

        // Show registering message
        Swal.fire({
          title: t('Registering...'),
          text: t('Please wait while we register your fingerprint'),
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
      };

      const performProgressiveScan = async () => {
        try {
          let scanData;

          // 1st scan attempt
          updateProgress(0, 1);
          scanData = await scanFingerprint();
          arr.push(scanData);
          if (scanData?.code !== '0000') {
            throw new Error(scanData?.message || 'Fingerprint scan failed');
          }

          // 2nd scan attempt
          updateProgress(10, 2);
          scanData = await scanFingerprint();
          arr.push(scanData);
          if (scanData?.code !== '0000') {
            throw new Error(scanData?.message || 'Fingerprint scan failed');
          }

          // 3rd scan attempt
          updateProgress(20, 3);
          scanData = await scanFingerprint();
          arr.push(scanData);
          if (scanData?.code !== '0000') {
            throw new Error(scanData?.message || 'Fingerprint scan failed');
          }

          // 4th scan attempt
          updateProgress(30, 4);
          scanData = await scanFingerprint();
          arr.push(scanData);
          if (scanData?.code !== '0000') {
            throw new Error(scanData?.message || 'Fingerprint scan failed');
          }

          // 5th scan attempt
          updateProgress(40, 5);
          scanData = await scanFingerprint();
          arr.push(scanData);
          if (scanData?.code !== '0000') {
            throw new Error(scanData?.message || 'Fingerprint scan failed');
          }

          // 6th scan attempt
          updateProgress(50, 6);
          scanData = await scanFingerprint();
          arr.push(scanData);
          if (scanData?.code !== '0000') {
            throw new Error(scanData?.message || 'Fingerprint scan failed');
          }

          // 7th scan attempt
          updateProgress(60, 7);
          scanData = await scanFingerprint();
          arr.push(scanData);
          if (scanData?.code !== '0000') {
            throw new Error(scanData?.message || 'Fingerprint scan failed');
          }

          // 8th scan attempt
          updateProgress(70, 8);
          scanData = await scanFingerprint();
          arr.push(scanData);
          if (scanData?.code !== '0000') {
            throw new Error(scanData?.message || 'Fingerprint scan failed');
          }

          // 9th scan attempt
          updateProgress(80, 9);
          scanData = await scanFingerprint();
          arr.push(scanData);
          if (scanData?.code !== '0000') {
            throw new Error(scanData?.message || 'Fingerprint scan failed');
          }

          // 10th scan attempt (final)
          updateProgress(90, 10);
          scanData = await scanFingerprint();
          arr.push(scanData);
          if (scanData?.code !== '0000') {
            throw new Error(scanData?.message || 'Fingerprint scan failed');
          }

          // All 10 scans successful
          updateProgress(100, 10);
          console.log('Final scan result:', scanData);
          scanResult = scanData;

          // 🎯 Call function when progress reaches 100%
          await onProgressComplete(scanData);

        } catch (error) {
          scanError = error;
        }
      };

      // Execute the progressive scan
      await performProgressiveScan();

      if (scanError) {
        throw scanError;
      }

      const template = extractFingerprintTemplate(scanResult);
      if (!template) {
        throw new Error('Fingerprint template is empty');
      }

      await registerFingerprintBiometric(userId, arr);
      await loadRegisteredBiometrics();

      // Close loading modal and show success
      Swal.close();
      Swal.fire({
        icon: 'success',
        title: t('Fingerprint registered successfully'),
        timer: 1800,
        customClass: {
          confirmButton: 'gradient-button'
        }
      });

    } catch (error) {
      Swal.close();
      if (error.response?.data?.message === "FINGERPRINT enrollment already completed") {
        Swal.fire({
          icon: 'error',
          title: t('Biometric already registered'),
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: t('Something went wrong'),
          text: error?.message || t('Please try again.'),
        });
      }
    } finally {
      setBiometricLoading(null);
    }
  };

  const getContentPadding = () => {
    if (isMobile) {
      return "";
    }
    return sidebarOpen
      ? (dir === 'rtl' ? 'pr-64' : 'pl-64')
      : (dir === 'rtl' ? 'pr-20' : 'pl-20');
  };

  useEffect(() => {
    loadRegisteredBiometrics();
  }, [userId, loadRegisteredBiometrics]);

  useEffect(() => {
    const updateSidebarState = () => {
      const sidebarState = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-open');
      setSidebarOpen(sidebarState === '1');
    };

    updateSidebarState();

    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    const observer = new MutationObserver(updateSidebarState);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    getProfileData();
  }, [getProfileData]);

  // Add these state variables
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBiometricType, setSelectedBiometricType] = useState(null);

  // Add these handler functions
  const handleDeleteBiometric = (type) => {
    setSelectedBiometricType(type);
    setShowDeleteModal(true);
  };

  const confirmDeleteBiometric = async () => {
    try {
      setBiometricLoading(selectedBiometricType);
      console.log(userId, selectedBiometricType);
      await deleteBiometric(userId, selectedBiometricType);
      setRegisteredBiometrics((prev) => ({ ...prev, [selectedBiometricType]: false }));
      Swal.fire({
        icon: 'success',
        title: t('Biometric deleted successfully'),
        timer: 1800,
        customClass: {
          confirmButton: 'gradient-button'
        }
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: t('Failed to delete biometric'),
      });
    } finally {
      setBiometricLoading(null);
      setShowDeleteModal(false);
      setSelectedBiometricType(null);
    }
  };

  const confirmDeleteBiometric2 = async () => {
    try {
      setBiometricLoading(selectedBiometricType);
      await deleteBiometric2(userId, selectedBiometricType);
      setRegisteredBiometrics((prev) => ({ ...prev, [selectedBiometricType]: false }));
      Swal.fire({
        icon: 'success',
        title: t('Biometric deleted successfully'),
        timer: 1800,
        customClass: {
          confirmButton: 'gradient-button'
        }
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: t('Failed to delete biometric'),
      });
    } finally {
      setBiometricLoading(null);
      setShowDeleteModal(false);
      setSelectedBiometricType(null);
    }
  };

  return (
    <div className={`m-4 transition-all duration-300 ${getContentPadding()}`} dir={dir}>
      {/* <FaceScanner loadRegisteredBiometrics={loadRegisteredBiometrics} /> */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => {
            navigate("/")
            sessionStorage.removeItem("conversation");
            sessionStorage.removeItem("currentSessionId");
            sessionStorage.removeItem("sessionNumber");
          }}
          style={{ backgroundColor: "#767676" }}
          className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center transition-all duration-300 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-xl text-white" />
        </button>
        <h2 className="text-xl font-medium text-gray-900">
          {t("My Profile")}
        </h2>
      </div>
      <div className="md:flex pb-28 items-center bg-gradient-to-r from-[#B03982] to-[#733C86] mb-4 p-3 text-white rounded-t-xl">
      </div>
      <div className="mx-auto p-4">

        <div className='flex items-center gap-4'>
          <div style={{ width: '80px', height: '80px' }} className='bg-gray-100 object-cover rounded-full border-4 border-[#B03982] overflow-hidden'>
            <div className="w-full h-full flex items-center justify-center text-black text-[18px] font-semibold">
              {profileData?.name
                ?.split(" ")
                .map(word => word[0])
                .slice(0, 2)
                .join("")
                .toUpperCase()}
            </div>
          </div>
          <div className='flex flex-col gap-1'>
            <p className="text-black text-[18px] font-semibold">{profileData.name}</p>
            <p className="text-gray-700 text-[16px] font-medium">{profileData.email}</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="mt-8">
          {/* Status Cards */}

          {/* Security Info Banner */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">{t('Secure Biometric Authentication')}</p>
                <p className="text-xs text-blue-600 mt-1">
                  {t('Your biometric data is encrypted and stored securely on your device. We never store your actual biometric data on our servers.')}
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Face Status Card */}
            <div className={`relative rounded-xl border-2 p-4 transition-all duration-200 ${registeredBiometrics.FACE
              ? 'border-green-200 bg-green-50'
              : 'border-gray-200 bg-gray-50'
              }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${registeredBiometrics.FACE
                    ? 'bg-green-100 text-green-600'
                    : 'bg-gray-200 text-gray-500'
                    }`}>
                    <ScanFace className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{t('Face Recognition')}</p>
                    <p className={`text-sm ${registeredBiometrics.FACE ? 'text-green-600' : 'text-gray-500'
                      }`}>
                      {registeredBiometrics.FACE ? t('Active') : t('Not Set Up')}
                    </p>
                  </div>
                </div>

                {registeredBiometrics.FACE ? (
                  <button
                    onClick={() => handleDeleteBiometric('FACE')}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
                    title={t('Delete Face Biometric')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={registerFace}
                    disabled={biometricLoading !== null}
                    className="px-4 py-2 bg-gradient-to-r from-[#A73884] to-[#803A85] text-white text-sm font-medium rounded-lg hover:shadow-lg hover:shadow-purple-300/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingFaceButton ? (
                      <div className="flex items-center space-x-2">
                        <span className="w-4 h-4 border-2 border-t-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      </div>
                    ) : (
                      t('Set Up')
                    )}
                  </button>
                )}
              </div>

              {registeredBiometrics.FACE && (
                <div className="mt-3 flex items-center gap-2 text-xs text-green-600">
                  <CheckCircle2 className="w-3 h-3" />
                  <span>{t('Face biometric configured and active')}</span>
                </div>
              )}
            </div>
            {/* Fingerprint Status Card */}
            {(window.screen.width === 1080 && window.screen.height === 1920) ? (
              <div className={`relative rounded-xl border-2 p-4 transition-all duration-200 ${registeredBiometrics.FINGERPRINT
                ? 'border-green-200 bg-green-50'
                : 'border-gray-200 bg-gray-50'
                }`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${registeredBiometrics.FINGERPRINT
                      ? 'bg-green-100 text-green-600'
                      : 'bg-gray-200 text-gray-500'
                      }`}>
                      <Fingerprint className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{t('Fingerprint')}</p>
                      <p className={`text-sm ${registeredBiometrics.FINGERPRINT ? 'text-green-600' : 'text-gray-500'
                        }`}>
                        {registeredBiometrics.FINGERPRINT ? t('Active') : t('Not Set Up')}
                      </p>
                    </div>
                  </div>

                  {registeredBiometrics.FINGERPRINT ? (
                    <button
                      onClick={() => handleDeleteBiometric('FINGERPRINT')}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
                      title={t('Delete Fingerprint')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={registerFingerprint}
                      disabled={biometricLoading !== null}
                      className="px-4 py-2 bg-gradient-to-r from-[#A73884] to-[#803A85] text-white text-sm font-medium rounded-lg hover:shadow-lg hover:shadow-purple-300/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingFingerprintButton ? (
                        <div className="flex items-center space-x-2">
                          <span className="w-4 h-4 border-2 border-t-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        </div>
                      ) : (
                        t('Set Up')
                      )}
                    </button>
                  )}

                </div>

                {registeredBiometrics.FINGERPRINT && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-green-600">
                    <CheckCircle2 className="w-3 h-3" />
                    <span>{t('Fingerprint biometric configured and active')}</span>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>

        {/* Camera Modal */}
        <CameraCapture
          isOpen={showCameraModal.isOpen}
          setShowCameraModal={setShowCameraModal}
          onClose={handleCameraClose}
          onCapture={handleFaceCapture}
          transactionId={showCameraModal.transactionId}
          userId={userId}
          setBiometricLoading={setBiometricLoading}
          loadRegisteredBiometrics={loadRegisteredBiometrics}
          t={t}
        />

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <DeleteConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={selectedBiometricType === 'FINGERPRINT' ? confirmDeleteBiometric : confirmDeleteBiometric2}
            biometricType={selectedBiometricType}
            t={t}
          />
        )}

        <form className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-10">

          <div className="col-span-1">
            <label htmlFor="name" className="mb-2 text-gray-700 font-medium block">{t("Employee Id")} <span className="text-red-500">*</span></label>
            <input id="name" type="text" value={profileData?.employeeId} disabled={true} className="w-full h-12 px-4 border-2 border-[#B03982] rounded-lg focus:outline-none focus:ring-0 focus:ring-[#B03982]" />
          </div>

          <div className="col-span-1">
            <label htmlFor="name" className="mb-2 text-gray-700 font-medium block">{t("Name")} <span className="text-red-500">*</span></label>
            <input id="name" type="text" value={profileData?.name} disabled={true} className="w-full h-12 px-4 border-2 border-[#B03982] rounded-lg focus:outline-none focus:ring-0 focus:ring-[#B03982]" />
          </div>

          <div className="col-span-1">
            <label htmlFor="name" className="mb-2 text-gray-700 font-medium block">{t("Email")} <span className="text-red-500">*</span></label>
            <input id="name" type="text" value={profileData?.email} disabled={true} className="w-full h-12 px-4 border-2 border-[#B03982] rounded-lg focus:outline-none focus:ring-0 focus:ring-[#B03982]" />
          </div>

          <div className="col-span-1">
            <label htmlFor="doj" className="mb-2 text-gray-700 font-medium block">{t("Company")}</label>
            <input id="doj" type="text" value={profileData?.company?.companyName} disabled={true} className="w-full h-12 px-4 border-2 border-[#B03982] rounded-lg focus:outline-none focus:ring-0 focus:ring-[#B03982]" />
          </div>

          <div className="col-span-1">
            <label htmlFor="doj" className="mb-2 text-gray-700 font-medium block">{t("Department")}</label>
            <input id="doj" type="text" value={profileData?.department?.departmentName} disabled={true} className="w-full h-12 px-4 border-2 border-[#B03982] rounded-lg focus:outline-none focus:ring-0 focus:ring-[#B03982]" />
          </div>

          <div className="col-span-1 md:col-span-2 xl:col-span-3 gap-2">
            <h3 className="text-lg font-bold text-gray-900">
              <span className="inline-block mr-3 w-1.5 h-6 rounded-full bg-[#B03982]" />{t("My email address")}
            </h3>
            <div className='flex items-center gap-4 mt-6'>
              <div className='w-12 h-12 rounded-full bg-[#B03982] flex justify-center items-center'>
                <SvgIcon name="email" size={25} />
              </div>
              <p className="text-black text-[16px] font-semibold">{profileData?.email || profileData.email}</p>
            </div>
          </div>

        </form>

        <h3 className="text-lg font-bold text-gray-900 mt-10 mb-5">
          <span className="inline-block mr-3 w-1.5 h-6 rounded-full bg-[#B03982]" />{t("Change Password")}
        </h3>

        <form className="grid grid-cols-1 md:grid-cols-3 gap-6">

          <div className="relative">
            <label htmlFor="name" className="mb-2 text-gray-700 font-medium block">{t("Old password")} <span className="text-red-500">*</span></label>
            <input
              dir={dir}
              type={showPasswords.oldPassword ? "text" : "password"}
              name="oldPassword"
              value={formData.oldPassword}
              onChange={handleChange}
              placeholder={t("current_password")}
              className="w-full h-12 px-4 border-2 border-[#B03982] rounded-lg focus:outline-none focus:ring-0 focus:ring-[#B03982]"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility("oldPassword")}
              className={`absolute ${dir === "rtl" ? 'left-3' : 'right-3'} top-1/2 translate-y-1/2 text-gray-500 hover:text-[#B03982]`}
            >
              {showPasswords.oldPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>

          <div className="relative">
            <label htmlFor="name" className="mb-2 text-gray-700 font-medium block">{t("Password")} <span className="text-red-500">*</span></label>
            <input
              dir={dir}
              type={showPasswords.password ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t("new_password")}
              className="w-full h-12 px-4 border-2 border-[#B03982] rounded-lg focus:outline-none focus:ring-0 focus:ring-[#B03982]"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility("password")}
              className={`absolute ${dir === "rtl" ? 'left-3' : 'right-3'} top-1/2 translate-y-1/2 text-gray-500 hover:text-[#B03982]`}
            >
              {showPasswords.password ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>

          <div className="relative">
            <label htmlFor="name" className="mb-2 text-gray-700 font-medium block">{t("Confirm Password")} <span className="text-red-500">*</span></label>
            <input
              dir={dir}
              type={showPasswords.confirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t("confirm_new_password")}
              className="w-full h-12 px-4 border-2 border-[#B03982] rounded-lg focus:outline-none focus:ring-0 focus:ring-[#B03982]"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility("confirmPassword")}
              className={`absolute ${dir === "rtl" ? 'left-3' : 'right-3'} top-1/2 translate-y-1/2 text-gray-500 hover:text-[#B03982]`}
            >
              {showPasswords.confirmPassword ? (
                <Eye className="w-5 h-5" />
              ) : (
                <EyeOff className="w-5 h-5" />
              )}
            </button>
          </div>

        </form>

        {error && <div className="text-red-500 mt-4">{error}</div>}

        <div className="flex mt-10">
          <button
            disabled={loading}
            onClick={handleSubmit}
            className={`
                px-6 py-3 rounded-xl font-medium text-white
                bg-gradient-to-r from-[#A73884] to-[#803A85]
                hover:shadow-lg hover:shadow-purple-300/50 transition-all duration-300
                flex items-center gap-2
                w-full sm:w-auto
            `}
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
              </>
            ) : t("Update password")}
          </button>
        </div>

      </div>
    </div>
  );
};

export default ProfileSettings;